import { jitterAngle } from "./physics/config";
import { effectiveCells, thrusterPortBlocked } from "./ship";
import {
  AMBIENT_K,
  radiativeCool,
  SPECIFIC_HEAT_J_PER_KG_K,
  T4_REF_RANGE_K4,
} from "./thermal";
import type { Ship, ShipModule, Spark, World } from "./types";

const SPARK_CULL_TEMP_K = 273.15 + 750;

const ETA_EJECT = 0.1;

const ETA_KIN = 5e-4;

const VISIBLE_SPARK_MASS_KG = 1e-3;

const SUPERHEAT_DT_K = 400;

const EJECT_VEL_NOISE_M_S = 4;

const STEFAN_BOLTZMANN = 5.670374419e-8;

const DEFAULT_SPARK_THERMAL_RADIUS_M = 0.012;
const DEFAULT_SPARK_EMISSIVITY = 0.4;

export function sparkHeatCapJPerK(sp: Spark): number {
  return sp.mass * SPECIFIC_HEAT_J_PER_KG_K;
}

function sparkRadiateCoefW(sp: Spark): number {
  const A = 4 * Math.PI * sp.thermalRadiusM * sp.thermalRadiusM;
  return sp.emissivity * STEFAN_BOLTZMANN * A * T4_REF_RANGE_K4;
}

/** Math.round would zero sub-0.5 spark counts; carry fraction as probability. */
function stochasticRound(x: number): number {
  if (x <= 0) return 0;
  const f = Math.floor(x);
  return f + (Math.random() < x - f ? 1 : 0);
}

/** baseVx/baseVy override surface velocity (collisions: post-impulse contact
 *  vel; beams: ship drift). etaBeam overrides ETA_EJECT. */
export function emitSparks(
  world: World,
  ship: Ship, m: ShipModule,
  hitX: number, hitY: number,
  faceDx: number, faceDy: number,
  E: number,
  baseVx?: number, baseVy?: number,
  etaBeam?: number,
): void {
  if (E <= 0) return;
  const def = m.def;
  const eff = effectiveCells(def, m.facing);
  if (thrusterPortBlocked(ship, m, eff.w, eff.h, faceDx, faceDy)) return;

  const c = Math.max(1, def.stats.thermalInertia);
  const meltK = def.stats.meltPointK;
  const dT = Math.max(1, meltK - AMBIENT_K);
  const ePerKg = c * dT;

  const T_birth = meltK + SUPERHEAT_DT_K;
  if (T_birth < SPARK_CULL_TEMP_K) return;

  const eta = etaBeam ?? ETA_EJECT;
  const mMelt = E / ePerKg;
  const n = stochasticRound(mMelt * eta / VISIBLE_SPARK_MASS_KG);
  if (n <= 0) return;

  const vEject = Math.sqrt(2 * ETA_KIN * ePerKg);

  const emissivity = def.stats.thermalEmissivity > 0
    ? def.stats.thermalEmissivity
    : DEFAULT_SPARK_EMISSIVITY;

  const rot = jitterAngle(ship.body.rot);
  const cr = Math.cos(rot);
  const sr = Math.sin(rot);
  const baseAng = jitterAngle(
    Math.atan2(faceDx * sr + faceDy * cr, faceDx * cr - faceDy * sr),
  );

  let vx: number, vy: number;
  if (baseVx !== undefined && baseVy !== undefined) {
    vx = baseVx; vy = baseVy;
  } else {
    const rx = hitX - ship.body.pos.x;
    const ry = hitY - ship.body.pos.y;
    vx = ship.body.vel.x - ship.body.angVel * ry;
    vy = ship.body.vel.y + ship.body.angVel * rx;
  }

  for (let i = 0; i < n; i++) {
    const ang = baseAng + (Math.random() - 0.5) * Math.PI * 0.55;
    const speed = vEject + (Math.random() - 0.5) * EJECT_VEL_NOISE_M_S;
    const dx = Math.cos(ang), dy = Math.sin(ang);
    const thermalRadiusM = DEFAULT_SPARK_THERMAL_RADIUS_M
      * (0.5 + Math.random());
    world.sparks.push({
      id: world.nextId++,
      pos: { x: hitX + dx * 0.2, y: hitY + dy * 0.2 },
      vel: { x: vx + dx * speed, y: vy + dy * speed },
      tempK: T_birth,
      mass: VISIBLE_SPARK_MASS_KG,
      emissivity,
      thermalRadiusM,
    });
  }
}

export function worldNormalToShipFace(
  ship: Ship, nx: number, ny: number,
): { dx: number; dy: number } {
  const rot = jitterAngle(-ship.body.rot);
  const cr = Math.cos(rot);
  const sr = Math.sin(rot);
  const lnx = nx * cr - ny * sr;
  const lny = nx * sr + ny * cr;
  if (Math.abs(lnx) >= Math.abs(lny)) return { dx: lnx >= 0 ? 1 : -1, dy: 0 };
  return { dx: 0, dy: lny >= 0 ? 1 : -1 };
}

export function coolSpark(sp: Spark, dt: number): boolean {
  sp.tempK = radiativeCool(sp.tempK, sparkHeatCapJPerK(sp), sparkRadiateCoefW(sp), dt);
  return sp.tempK <= SPARK_CULL_TEMP_K;
}
